import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Clock, LogIn, LogOut, Loader2, Navigation, CalendarDays, ExternalLink, FileDown, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";

const STATUSES = ["present","absent","leave","half_day"];
const color = {
  present: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  absent: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  leave: "bg-muted text-muted-foreground border-border",
  half_day: "bg-amber-500/15 text-amber-600 border-amber-500/30",
};

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" }); } catch { return iso || "—"; } };
const mapUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

// Reverse geocode via free OpenStreetMap Nominatim (no API key required).
async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return "";
    const j = await res.json();
    return j.display_name || "";
  } catch { return ""; }
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("Geolocation is not supported by this browser.")); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  });
}

// ---------------- My Attendance (self-service GPS punch) ----------------
function MyAttendanceCard({ onUpdated }) {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/attendance/me?month=${month}`);
      setMe({ employee: data.employee, today: data.today });
      setRows(data.rows || []);
    } catch (e) {
      // silently ignore — user might not be linked to an employee
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const punch = async (kind) => {
    setBusy(true); setBusyLabel(kind === "in" ? "Checking in…" : "Checking out…");
    try {
      const pos = await getPosition();
      const { latitude, longitude, accuracy } = pos.coords;
      const address = await reverseGeocode(latitude, longitude);
      const payload = { lat: latitude, lng: longitude, accuracy, address };
      const path = kind === "in" ? "/attendance/check-in" : "/attendance/check-out";
      const { data } = await api.post(path, payload);
      if (kind === "in") {
        const fence = data.check_in?.fence;
        if (fence?.off_site) {
          toast.warning(`Checked in ${data.check_in ? `at ${fmtTime(data.check_in.at)}` : ""} · ${fence.distance_m}m from ${fence.project_name} (fence ${fence.radius_m}m)`);
        } else {
          toast.success(fence ? `Checked in at ${fmtTime(data.check_in.at)} · on-site at ${fence.project_name}` : `Checked in at ${fmtTime(data.check_in?.at)}`);
        }
      } else {
        toast.success(`Checked out · ${data.hours}h worked`);
      }
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      const msg = e?.response?.data?.detail || (e?.code === 1 ? "Location permission denied. Please allow location access." : e?.message) || "Failed";
      toast.error(msg);
    } finally { setBusy(false); setBusyLabel(""); }
  };

  const exportMyAttendance = async (fmt) => {
    if (!me?.employee?.id) return;
    try {
      const token = localStorage.getItem("woodland_token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/attendance/${me.employee.id}/export.xlsx?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `Attendance_${(me.employee.name || "me").replace(/[^A-Za-z0-9]+/g, "_")}_${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("XLSX downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const exportMyAttendancePdf = () => {
    if (!me?.employee) return;
    const totalHours = rows.reduce((s, r) => s + Number(r.hours || 0), 0);
    const body = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px">
        <div>
          <h1 style="margin:0">Monthly Attendance</h1>
          <div style="color:#73706A;font-size:11px;margin-top:6px">Employee: <b style="color:#1A1918">${me.employee.name}</b> · ${me.employee.department || ""}</div>
          <div style="color:#73706A;font-size:11px">Period: ${month}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#73706A">
          <div>Total hours: <b style="color:#1A1918">${totalHours.toFixed(2)}</b></div>
          <div>Present: <b style="color:#1A1918">${summary.present}</b> · Half days: <b style="color:#1A1918">${summary.half}</b></div>
        </div>
      </div>
      <table><thead><tr><th>Date</th><th>Check-in</th><th>Check-out</th><th class="right">Hours</th><th>Status</th><th>Location</th></tr></thead><tbody>
      ${rows.map(r => `<tr><td>${r.date}</td><td>${r.check_in ? fmtTime(r.check_in.at) : "—"}</td><td>${r.check_out ? fmtTime(r.check_out.at) : "—"}</td><td class="right">${Number(r.hours || 0).toFixed(2)}</td><td style="text-transform:capitalize">${(r.status || "").replace("_"," ")}</td><td style="font-size:10px">${(r.check_in?.address) || (r.check_in ? `${r.check_in.lat?.toFixed(4)}, ${r.check_in.lng?.toFixed(4)}` : "—")}</td></tr>`).join("")}
      ${rows.length === 0 ? '<tr><td colspan="6" style="text-align:center;color:#73706A;padding:20px">No attendance recorded this month.</td></tr>' : ""}
      </tbody></table>
    `;
    openBrandedPrintWindow(body, {
      subject: `Attendance — ${me.employee.name} — ${month}`,
      title: "Monthly Attendance",
      number: me.employee.name,
      date: month,
    });
  };

  const today = me?.today;
  const employee = me?.employee;

  const summary = useMemo(() => {
    const totalHours = rows.reduce((s, r) => s + Number(r.hours || 0), 0);
    const present = rows.filter(r => r.status === "present").length;
    const half = rows.filter(r => r.status === "half_day").length;
    return { totalHours: Math.round(totalHours * 10) / 10, present, half, days: rows.length };
  }, [rows]);

  if (me && !employee) {
    return (
      <div className="rounded-2xl p-6 bg-card border border-border/60" data-testid="my-attendance-unlinked">
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">My Attendance</div>
        <div className="text-sm">Your login isn&rsquo;t linked to an employee record yet — ask HR to add an employee with your email so you can punch in.</div>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="my-attendance">
      {/* Punch card */}
      <div className="lg:col-span-2 rounded-2xl p-6 bg-card border border-border/60">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">My Attendance · GPS</div>
            <div className="font-display text-2xl font-semibold" data-testid="my-name">{employee?.name || "—"}</div>
            <div className="text-xs text-muted-foreground capitalize">{(employee?.role || "").replace(/_/g, " ")}{employee?.department ? ` · ${employee.department}` : ""}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Today</div>
            <div className="text-sm">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long" })}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl border border-border/60 p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5"><LogIn className="w-3 h-3" /> Check-in</div>
            <div className="mt-1.5 font-display text-xl" data-testid="my-checkin-time">{today?.check_in ? fmtTime(today.check_in.at) : "—"}</div>
            {today?.check_in && (
              <div className="mt-1 text-[11px] text-muted-foreground truncate" title={today.check_in.address}>
                <MapPin className="w-3 h-3 inline mr-1" />
                {today.check_in.address ? today.check_in.address.split(",").slice(0, 3).join(",") : `${today.check_in.lat?.toFixed(5)}, ${today.check_in.lng?.toFixed(5)}`}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-border/60 p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5"><LogOut className="w-3 h-3" /> Check-out</div>
            <div className="mt-1.5 font-display text-xl" data-testid="my-checkout-time">{today?.check_out ? fmtTime(today.check_out.at) : "—"}</div>
            {today?.check_out && (
              <div className="mt-1 text-[11px] text-muted-foreground truncate" title={today.check_out.address}>
                <MapPin className="w-3 h-3 inline mr-1" />
                {today.check_out.address ? today.check_out.address.split(",").slice(0, 3).join(",") : `${today.check_out.lat?.toFixed(5)}, ${today.check_out.lng?.toFixed(5)}`}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          {!today?.check_in ? (
            <Button data-testid="btn-check-in" onClick={() => punch("in")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{busyLabel}</> : <><Navigation className="w-4 h-4 mr-2" />Check in with GPS</>}
            </Button>
          ) : !today?.check_out ? (
            <Button data-testid="btn-check-out" onClick={() => punch("out")} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white h-11 px-6">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{busyLabel}</> : <><LogOut className="w-4 h-4 mr-2" />Check out</>}
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-500" /> Attendance closed for today · <b className="text-foreground">{today.hours}h worked</b></div>
          )}
          {today?.check_in && (
            <a href={mapUrl(today.check_in.lat, today.check_in.lng)} target="_blank" rel="noreferrer" data-testid="my-map-link" className="text-xs text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View on map</a>
          )}
        </div>

        {today?.check_in?.fence?.off_site && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400" data-testid="my-off-site-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <b>Off-site check-in</b> — you punched in {today.check_in.fence.distance_m}m from <b>{today.check_in.fence.project_name}</b> (allowed radius {today.check_in.fence.radius_m}m). HR has been notified.
            </div>
          </div>
        )}
      </div>

      {/* Month summary */}
      <div className="rounded-2xl p-6 bg-card border border-border/60">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Month summary</div>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="my-month-picker"
            className="h-7 rounded border border-border/60 bg-background text-xs px-2" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border/50 p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Present</div>
            <div className="font-display text-2xl mt-1">{summary.present}</div>
          </div>
          <div className="rounded-lg border border-border/50 p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Half days</div>
            <div className="font-display text-2xl mt-1">{summary.half}</div>
          </div>
          <div className="rounded-lg border border-border/50 p-3 col-span-2">
            <div className="text-[10px] uppercase text-muted-foreground">Total hours</div>
            <div className="font-display text-2xl mt-1">{summary.totalHours} <span className="text-xs text-muted-foreground">h</span></div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="outline" className="flex-1" onClick={exportMyAttendancePdf} data-testid="my-export-pdf-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" className="flex-1" onClick={() => exportMyAttendance("xlsx")} data-testid="my-export-xlsx-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> XLSX</Button>
        </div>
      </div>

      {/* Month history — full width */}
      <div className="lg:col-span-3 rounded-2xl bg-card border border-border/60 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2 text-sm">
          <CalendarDays className="w-4 h-4 text-muted-foreground" /> My history · {month}
        </div>
        <div className="max-h-[280px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="text-left py-2.5 px-4">Date</th>
                <th className="text-left">Check-in</th>
                <th className="text-left">Check-out</th>
                <th className="text-right">Hours</th>
                <th className="text-left">Status</th>
                <th className="text-left">Location</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No attendance recorded this month yet.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} data-testid={`my-att-${r.date}`} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="py-2.5 px-4 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td>{r.check_in ? fmtTime(r.check_in.at) : "—"}</td>
                  <td>{r.check_out ? fmtTime(r.check_out.at) : "—"}</td>
                  <td className="text-right font-medium">{Number(r.hours || 0).toFixed(2)}</td>
                  <td><span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${color[r.status] || "border-border"}`}>{(r.status || "").replace("_"," ")}</span></td>
                  <td className="text-xs text-muted-foreground truncate max-w-[240px]">{r.check_in?.address || (r.check_in ? `${r.check_in.lat?.toFixed(4)}, ${r.check_in.lng?.toFixed(4)}` : "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- Admin GPS Attendance table ----------------
function GpsAttendanceTable({ date }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState(null); // { title, lat, lng, address, at }

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/attendance/gps?date=${date}`); setRows(data.rows || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const downloadEmpXlsx = async (empId, name) => {
    try {
      const month = date.slice(0, 7);
      const token = localStorage.getItem("woodland_token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backend}/api/attendance/${empId}/export.xlsx?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `Attendance_${(name || "employee").replace(/[^A-Za-z0-9]+/g, "_")}_${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`XLSX downloaded for ${name}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="admin-gps-table">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
        <div className="text-sm font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> GPS Check-ins · {date}</div>
        <div className="text-xs text-muted-foreground">{rows.length} entries {loading && "· loading…"}</div>
      </div>
      <div className="overflow-auto max-h-[520px]">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr>
              <th className="text-left py-2.5 px-4">Employee</th>
              <th className="text-left">Role</th>
              <th className="text-left">Check-in</th>
              <th className="text-left">Check-out</th>
              <th className="text-right">Hours</th>
              <th className="text-left">Status</th>
              <th className="text-left">Location</th>
              <th className="text-right pr-3">Export</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center py-10 text-sm text-muted-foreground">No GPS attendance recorded for this day.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} data-testid={`gps-row-${r.id}`} className="border-t border-border/50 hover:bg-muted/20">
                <td className="py-2.5 px-4">
                  <div className="font-medium">{r.employee_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate max-w-[200px]">{r.employee_department}</div>
                </td>
                <td className="capitalize text-muted-foreground">{(r.employee_role || "").replace("_"," ")}</td>
                <td>{r.check_in ? fmtTime(r.check_in.at) : "—"}</td>
                <td>{r.check_out ? fmtTime(r.check_out.at) : "—"}</td>
                <td className="text-right font-medium">{Number(r.hours || 0).toFixed(2)}</td>
                <td><span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${color[r.status] || "border-border"}`}>{(r.status || "").replace("_"," ")}</span></td>
                <td>
                  {r.check_in ? (
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`gps-pin-${r.id}`}
                        onClick={() => setPin({ title: r.employee_name, kind: "Check-in", lat: r.check_in.lat, lng: r.check_in.lng, address: r.check_in.address, at: r.check_in.at, accuracy: r.check_in.accuracy })}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      ><MapPin className="w-3 h-3" /> View</button>
                      <a href={mapUrl(r.check_in.lat, r.check_in.lng)} target="_blank" rel="noreferrer" data-testid={`gps-map-${r.id}`} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Maps</a>
                      {r.check_in.fence?.off_site && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400" title={`${r.check_in.fence.distance_m}m from ${r.check_in.fence.project_name}`}>
                          <AlertTriangle className="w-3 h-3" /> Off-site
                        </span>
                      )}
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="pr-3 text-right">
                  <button onClick={() => downloadEmpXlsx(r.employee_id, r.employee_name)} data-testid={`gps-export-${r.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Export monthly XLSX">
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pin} onOpenChange={(o) => !o && setPin(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{pin?.title} · {pin?.kind} at {pin ? fmtTime(pin.at) : ""}</DialogTitle></DialogHeader>
          {pin && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">{pin.address || "Address not resolved"}</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg border border-border/50 p-3"><div className="text-[10px] uppercase text-muted-foreground">Latitude</div><div className="font-mono mt-0.5">{pin.lat}</div></div>
                <div className="rounded-lg border border-border/50 p-3"><div className="text-[10px] uppercase text-muted-foreground">Longitude</div><div className="font-mono mt-0.5">{pin.lng}</div></div>
                <div className="rounded-lg border border-border/50 p-3"><div className="text-[10px] uppercase text-muted-foreground">Accuracy</div><div className="font-mono mt-0.5">{pin.accuracy ? `±${Math.round(pin.accuracy)} m` : "—"}</div></div>
              </div>
              <div className="rounded-xl overflow-hidden border border-border/60 bg-muted/30 aspect-video">
                <iframe
                  title="map"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${pin.lng-0.01}%2C${pin.lat-0.007}%2C${pin.lng+0.01}%2C${pin.lat+0.007}&layer=mapnik&marker=${pin.lat}%2C${pin.lng}`}
                  className="w-full h-full"
                  style={{ border: 0 }}
                />
              </div>
              <a href={mapUrl(pin.lat, pin.lng)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Open in Google Maps</a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Main page ----------------
export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = ["super_admin","director","hr"].includes(user?.role);
  const [emps, setEmps] = useState([]);
  const [att, setAtt] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    const e = await api.get("/employees"); setEmps(e.data);
    const a = await api.get(`/attendance?date=${date}`); setAtt(a.data);
  };
  useEffect(() => { load();   }, [date]);

  const mark = async (emp, status) => {
    await api.post("/attendance", { employee_id: emp.id, date, status, hours: status === "present" ? 8 : status === "half_day" ? 4 : 0 });
    load();
  };

  const statusOf = (emp) => att.find((a) => a.employee_id === emp.id)?.status;
  const summary = STATUSES.reduce((acc, s) => { acc[s] = att.filter((a) => a.status === s).length; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">HR · GPS Attendance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Attendance</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Viewing</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="max-w-[180px]" data-testid="att-date-picker" />
        </div>
      </div>

      {/* Every logged-in employee sees their own punch card */}
      <MyAttendanceCard onUpdated={load} />

      {isAdmin && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATUSES.map((s) => (
              <div key={s} className="rounded-2xl p-5 bg-card border border-border/60">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground capitalize">{s.replace("_"," ")}</div>
                <div className="font-display text-3xl font-semibold mt-2">{summary[s] || 0}</div>
              </div>
            ))}
          </div>

          <GpsAttendanceTable date={date} />

          <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="manual-attendance-table">
            <div className="px-4 py-3 border-b border-border/50 text-sm font-medium">Manual override</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <tr><th className="text-left py-3 px-4">Employee</th><th className="text-left">Role</th><th className="text-left">Status</th></tr>
              </thead>
              <tbody>
                {emps.map((e) => {
                  const st = statusOf(e);
                  return (
                    <tr key={e.id} data-testid={`row-${e.id}`} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4 font-medium">{e.name}</td>
                      <td className="capitalize text-muted-foreground">{e.role?.replace("_"," ")}</td>
                      <td>
                        <Select value={st || ""} onValueChange={(v) => mark(e, v)}>
                          <SelectTrigger className={`h-8 w-32 text-[11px] ${st ? color[st] : ""}`} data-testid={`status-${e.id}`}><SelectValue placeholder="Mark" /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
